import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../common/hooks/useAuth';
import { useProducer } from '../../common/hooks/useProducer';
import { producerAPI, geocodingAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Input from '../../common/components/Input';
import Card from '../../common/components/Card';
import MapPicker from '../../common/components/MapPicker';
import toast from 'react-hot-toast';
import ukData from '../../data/uk_counties_towns.json';
import './RegisterProducerScreen.css';

const STAGES = {
  ACCOUNT: 1,
  FARM_NAME: 2,
  ADDRESS: 3,
  LOCATION: 4,
  DESCRIPTION: 5,
  SOCIAL: 6,
};

const STAGE_TITLES = {
  [STAGES.ACCOUNT]: 'Create Your Account',
  [STAGES.FARM_NAME]: 'Farm Details',
  [STAGES.ADDRESS]: 'Farm Address',
  [STAGES.LOCATION]: 'Confirm Location',
  [STAGES.DESCRIPTION]: 'Farm Description',
  [STAGES.SOCIAL]: 'Contact & Social Media',
};

const COUNTRIES = ['England', 'Wales', 'Scotland', 'Northern Ireland'];

const RegisterProducerScreen = () => {
  const navigate = useNavigate();
  const { user, register: registerAuth } = useAuth();
  const { register: registerProducer, isRegistering } = useProducer();

  // Current stage
  const [currentStage, setCurrentStage] = useState(STAGES.ACCOUNT);

  // Stage 1: Account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountCreating, setAccountCreating] = useState(false);

  // Stage 2: Farm Name
  const [farmName, setFarmName] = useState('');
  const [urlFriendlyName, setUrlFriendlyName] = useState('');
  const [takenUrlNames, setTakenUrlNames] = useState([]);
  const [urlNameError, setUrlNameError] = useState('');

  // Stage 3: Address
  const [postcode, setPostcode] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [town, setTown] = useState('');
  const [county, setCounty] = useState('');
  const [country, setCountry] = useState('');
  const [farmVisits, setFarmVisits] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);

  // Get available counties and towns based on selected country
  const [availableCounties, setAvailableCounties] = useState([]);
  const [availableTowns, setAvailableTowns] = useState([]);
  const [townInputValue, setTownInputValue] = useState('');
  const [countyInputValue, setCountyInputValue] = useState('');
  const [showTownSuggestions, setShowTownSuggestions] = useState(false);
  const [showCountySuggestions, setShowCountySuggestions] = useState(false);
  const townDropdownRef = useRef(null);
  const countyDropdownRef = useRef(null);

  // Stage 4: Location
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // Stage 5: Description
  const [farmDescription, setFarmDescription] = useState('');

  // Stage 6: Social
  const [contactPhone, setContactPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');

  // Success dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [registeredFarmData, setRegisteredFarmData] = useState(null);

  // If user is already authenticated, skip to stage 2
  useEffect(() => {
    if (user) {
      setCurrentStage(STAGES.FARM_NAME);
      setEmail(user.email);
    }
  }, [user]);

  // Fetch URL names when component mounts
  useEffect(() => {
    const fetchUrlNames = async () => {
      try {
        const response = await producerAPI.getAllUrlFriendlyNames();
        setTakenUrlNames(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch URL names:', error);
      }
    };
    fetchUrlNames();
  }, []);

  // Update available counties when country changes
  useEffect(() => {
    if (country && ukData[country]) {
      const counties = Object.keys(ukData[country]);
      setAvailableCounties(counties);
    } else {
      setAvailableCounties([]);
    }
  }, [country]);

  // Update available towns when county changes
  useEffect(() => {
    if (country && county && ukData[country] && ukData[country][county]) {
      setAvailableTowns(ukData[country][county]);
    } else {
      setAvailableTowns([]);
    }
  }, [country, county]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (townDropdownRef.current && !townDropdownRef.current.contains(event.target)) {
        setShowTownSuggestions(false);
      }
      if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target)) {
        setShowCountySuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Validate URL-friendly name
  useEffect(() => {
    if (!urlFriendlyName) {
      setUrlNameError('');
      return;
    }

    if (urlFriendlyName.length < 3) {
      setUrlNameError('URL name must be at least 3 characters');
      return;
    }

    if (takenUrlNames.includes(urlFriendlyName)) {
      setUrlNameError('This URL name is already taken');
    } else {
      setUrlNameError('');
    }
  }, [urlFriendlyName, takenUrlNames]);

  // Generate URL-friendly name from farm name
  const generateUrlFriendlyName = (farmName) => {
    return farmName
      .toLowerCase()
      .trim()
      .replace(/['''"""]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  };

  const handleFarmNameChange = (value) => {
    setFarmName(value);
    const friendly = generateUrlFriendlyName(value);
    setUrlFriendlyName(friendly);
  };

  const handleUrlNameChange = (value) => {
    const sanitized = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');
    setUrlFriendlyName(sanitized);
  };

  // Format UK postcode
  const formatPostcode = (value) => {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase();

    // UK postcode pattern: 2-4 chars, then 3 chars (e.g., SW1A 1AA)
    if (cleaned.length <= 4) {
      return cleaned;
    }

    // Insert space before last 3 characters
    const outward = cleaned.slice(0, -3);
    const inward = cleaned.slice(-3);
    return `${outward} ${inward}`;
  };

  const handlePostcodeChange = (value) => {
    const formatted = formatPostcode(value);
    setPostcode(formatted);
  };

  // Handle county combo box
  const handleCountyInputChange = (value) => {
    setCountyInputValue(value);
    setCounty(value);
    setShowCountySuggestions(true);
  };

  const handleCountySelect = (selectedCounty) => {
    setCounty(selectedCounty);
    setCountyInputValue(selectedCounty);
    setShowCountySuggestions(false);
    // Reset town when county changes
    setTown('');
    setTownInputValue('');
  };

  const getFilteredCounties = () => {
    if (!countyInputValue) return availableCounties;
    return availableCounties.filter(c =>
      c.toLowerCase().includes(countyInputValue.toLowerCase())
    );
  };

  // Handle town combo box
  const handleTownInputChange = (value) => {
    setTownInputValue(value);
    setTown(value);
    setShowTownSuggestions(true);
  };

  const handleTownSelect = (selectedTown) => {
    setTown(selectedTown);
    setTownInputValue(selectedTown);
    setShowTownSuggestions(false);
  };

  const getFilteredTowns = () => {
    if (!townInputValue) return availableTowns;
    return availableTowns.filter(t =>
      t.toLowerCase().includes(townInputValue.toLowerCase())
    );
  };

  // Stage 1: Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setAccountCreating(true);
    try {
      await registerAuth(email, password);
      toast.success('Account created successfully!');
      setCurrentStage(STAGES.FARM_NAME);
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
    } finally {
      setAccountCreating(false);
    }
  };

  // Navigation handlers
  const handleNext = async () => {
    // Validate current stage before moving forward
    if (currentStage === STAGES.FARM_NAME) {
      if (!farmName.trim()) {
        toast.error('Please enter your farm name');
        return;
      }
      if (!urlFriendlyName || urlNameError) {
        toast.error('Please fix the URL name error');
        return;
      }
    }

    if (currentStage === STAGES.ADDRESS) {
      if (!country) {
        toast.error('Please select a country');
        return;
      }
      if (!addressLine1.trim() || !town.trim() || !county.trim() || !postcode.trim()) {
        toast.error('Please fill in all required address fields');
        return;
      }

      // Geocode the address to get coordinates
      try {
        // First try: Build search query with postcode and address line 1 for better accuracy
        const searchQuery = `${addressLine1}, ${postcode}`;
        console.log(`Geocoding address: ${searchQuery}, ${country}`);

        let response = await geocodingAPI.searchAddresses(searchQuery, country);
        let addresses = response.data.data || [];

        // If postcode search fails, try with just the town and county
        if (addresses.length === 0) {
          const townQuery = `${town}, ${county}`;
          console.log(`Postcode not found, trying with town: ${townQuery}, ${country}`);

          response = await geocodingAPI.searchAddresses(townQuery, country);
          addresses = response.data.data || [];

          if (addresses.length > 0) {
            toast.success('Location found using town. Please verify on the map.');
          }
        }

        if (addresses.length > 0) {
          const address = addresses[0];
          setLatitude(address.latitude);
          setLongitude(address.longitude);
          console.log(`Geocoded to: ${address.latitude}, ${address.longitude}`);
        } else {
          toast.error('Could not find location for this address. Please verify your details.');
          return;
        }
      } catch (error) {
        console.error('Geocoding failed:', error);
        toast.error('Failed to find location for address');
        return;
      }
    }

    if (currentStage === STAGES.LOCATION) {
      if (!latitude || !longitude) {
        toast.error('Please confirm your farm location on the map');
        return;
      }
    }

    setCurrentStage(currentStage + 1);
  };

  const handleBack = () => {
    if (currentStage > STAGES.ACCOUNT) {
      setCurrentStage(currentStage - 1);
    }
  };

  // Final submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.email) {
      toast.error('User email is not available. Please try again.');
      return;
    }

    const registrationData = {
      farmName,
      farmDescription: farmDescription || '',
      addressLine1,
      addressLine2: addressLine2 || '',
      town,
      county,
      postcode,
      country,
      contactPhone: contactPhone || '',
      urlFriendlyName,
      contactEmail: user.email,
      latitude,
      longitude,
      instagram: instagram || '',
      facebook: facebook || '',
      twitter: twitter || '',
      farmVisits: farmVisits ? 1 : 0,
      shopOpen: shopOpen ? 1 : 0,
    };

    console.log('📤 Submitting producer registration:', registrationData);

    registerProducer(
      registrationData,
      {
        onSuccess: (response) => {
          console.log('✅ Registration successful:', response);
          const producerData = response?.data?.data;
          setRegisteredFarmData(producerData);
          setShowSuccessDialog(true);
        },
        onError: (error) => {
          console.error('❌ Registration error:', error);
          toast.error(error.message || 'Failed to register farm');
          if (error.details) {
            console.error('📋 Validation details:');
            error.details.forEach(detail => {
              console.error(`  - ${detail.field}: ${detail.message}`);
            });
          }
        },
      }
    );
  };

  // Render current stage
  const renderStage = () => {
    switch (currentStage) {
      case STAGES.ACCOUNT:
        return (
          <form onSubmit={handleCreateAccount}>
            <div className="form-section">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="stage-actions">
              <Button type="submit" loading={accountCreating} size="large" style={{ width: '100%' }}>
                Create Account & Continue
              </Button>
            </div>
          </form>
        );

      case STAGES.FARM_NAME:
        return (
          <div className="form-section">
            <Input
              label="Farm Name"
              value={farmName}
              onChange={(e) => handleFarmNameChange(e.target.value)}
              placeholder="e.g., Sunny Acres Farm"
              required
            />

            <div className="form-group">
              <label htmlFor="urlFriendlyName">
                URL-Friendly Name
                {urlNameError && <span className="error-text"> - {urlNameError}</span>}
              </label>
              <input
                id="urlFriendlyName"
                type="text"
                value={urlFriendlyName}
                onChange={(e) => handleUrlNameChange(e.target.value)}
                placeholder="e.g., sunny-acres-farm"
                required
                className={urlNameError ? 'error' : ''}
              />
              <small className="help-text">
                This will be your farm's URL: Producers.FarmFirst.uk/{urlFriendlyName || 'your-farm-name'}
              </small>
              {!urlNameError && urlFriendlyName && (
                <small className="success-text">✓ This name is available</small>
              )}
            </div>

            <div className="stage-actions">
              <Button type="button" onClick={handleNext} size="large" style={{ width: '100%' }}>
                Next
              </Button>
            </div>
          </div>
        );

      case STAGES.ADDRESS:
        return (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="country">Country</label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              >
                <option value="">Select a country</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="form-group combo-container" ref={countyDropdownRef}>
              <label htmlFor="county">County</label>
              <input
                id="county"
                type="text"
                value={countyInputValue}
                onChange={(e) => handleCountyInputChange(e.target.value)}
                onFocus={() => setShowCountySuggestions(true)}
                placeholder="Select or type county"
                required
              />
              {showCountySuggestions && getFilteredCounties().length > 0 && (
                <div className="combo-suggestions">
                  {getFilteredCounties().map((c, index) => (
                    <div
                      key={index}
                      className="combo-suggestion-item"
                      onClick={() => handleCountySelect(c)}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group combo-container" ref={townDropdownRef}>
              <label htmlFor="town">Town</label>
              <input
                id="town"
                type="text"
                value={townInputValue}
                onChange={(e) => handleTownInputChange(e.target.value)}
                onFocus={() => setShowTownSuggestions(true)}
                placeholder="Select or type town"
                required
              />
              {showTownSuggestions && getFilteredTowns().length > 0 && (
                <div className="combo-suggestions">
                  {getFilteredTowns().slice(0, 50).map((t, index) => (
                    <div
                      key={index}
                      className="combo-suggestion-item"
                      onClick={() => handleTownSelect(t)}
                    >
                      {t}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Input
              label="Postcode"
              value={postcode}
              onChange={(e) => handlePostcodeChange(e.target.value)}
              placeholder="e.g., SW1A 1AA"
              required
            />

            <Input
              label="Address Line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Street address"
              required
            />

            <Input
              label="Address Line 2 (Optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Building, unit, etc."
            />

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <span>Farm Visits Allowed</span>
                <input
                  type="checkbox"
                  checked={farmVisits}
                  onChange={(e) => setFarmVisits(e.target.checked)}
                />
                <div className="toggle-switch"></div>
              </label>
              <label className="checkbox-label">
                <span>Shop Open to Public</span>
                <input
                  type="checkbox"
                  checked={shopOpen}
                  onChange={(e) => setShopOpen(e.target.checked)}
                />
                <div className="toggle-switch"></div>
              </label>
            </div>

            <div className="stage-actions">
              <Button type="button" onClick={handleBack} variant="secondary">
                Back
              </Button>
              <Button type="button" onClick={handleNext}>
                Next
              </Button>
            </div>
          </div>
        );

      case STAGES.LOCATION:
        return (
          <div className="form-section">
            <div className="location-info">
              <p>Please confirm your farm's location on the map. You can drag the marker to adjust the position.</p>
              {latitude && longitude && (
                <p className="coordinates">
                  Coordinates: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              )}
            </div>

            <MapPicker
              latitude={latitude}
              longitude={longitude}
              onLocationChange={(lat, lng) => {
                setLatitude(lat);
                setLongitude(lng);
              }}
              height="400px"
            />

            <div className="stage-actions">
              <Button type="button" onClick={handleBack} variant="secondary">
                Back
              </Button>
              <Button type="button" onClick={handleNext}>
                Confirm Location
              </Button>
            </div>
          </div>
        );

      case STAGES.DESCRIPTION:
        return (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="farmDescription">Farm Description</label>
              <textarea
                id="farmDescription"
                value={farmDescription}
                onChange={(e) => setFarmDescription(e.target.value)}
                placeholder="Tell us about your farm, what you grow, your farming practices..."
                rows="6"
              />
              <small className="help-text">This will be displayed on your public farm page</small>
            </div>

            <div className="stage-actions">
              <Button type="button" onClick={handleBack} variant="secondary">
                Back
              </Button>
              <Button type="button" onClick={handleNext}>
                Next
              </Button>
            </div>
          </div>
        );

      case STAGES.SOCIAL:
        return (
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <p className="section-intro">Add your contact details and social media links (all optional)</p>

              <Input
                label="Phone Number (Optional)"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="e.g., 01234 567890"
              />

              <Input
                label="Instagram (Optional)"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="e.g., @yourfarm"
              />

              <Input
                label="Facebook (Optional)"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                placeholder="e.g., facebook.com/yourfarm"
              />

              <Input
                label="Twitter (Optional)"
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
                placeholder="e.g., @yourfarm"
              />
            </div>

            <div className="stage-actions">
              <Button type="button" onClick={handleBack} variant="secondary">
                Back
              </Button>
              <Button type="submit" loading={isRegistering} size="large">
                Complete Registration
              </Button>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <div className="register-producer-screen">
      <div className="register-container">
        <div className="register-header">
          <h1>Producer Registration</h1>
          <p>{STAGE_TITLES[currentStage]}</p>
        </div>

        {/* Progress indicator */}
        {currentStage > STAGES.ACCOUNT && (
          <div className="progress-indicator">
            {[STAGES.FARM_NAME, STAGES.ADDRESS, STAGES.LOCATION, STAGES.DESCRIPTION, STAGES.SOCIAL].map((stage) => (
              <div
                key={stage}
                className={`progress-step ${currentStage >= stage ? 'active' : ''} ${currentStage === stage ? 'current' : ''}`}
              >
                <div className="progress-step-number">{stage - 1}</div>
              </div>
            ))}
          </div>
        )}

        <Card>
          {renderStage()}
        </Card>
      </div>

      {/* Success Dialog */}
      {showSuccessDialog && registeredFarmData && (
        <div className="modal-overlay">
          <div className="success-dialog">
            <div className="success-dialog-header">
              <div className="success-icon">✓</div>
              <h2>Registration Complete!</h2>
            </div>
            <div className="success-dialog-content">
              <p className="success-message">
                Congratulations! Your farm has been successfully registered on FarmFirst.
              </p>
              <div className="producer-details">
                <h3>Farm Details</h3>
                <div className="detail-row">
                  <span className="detail-label">Farm Name:</span>
                  <span className="detail-value">{registeredFarmData.farm_name || farmName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Your Farm URL:</span>
                  <span className="detail-value">
                    Producers.FarmFirst.uk/{registeredFarmData.url_friendly_name || urlFriendlyName}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">{town}, {county}</span>
                </div>
              </div>
            </div>
            <div className="success-dialog-actions">
              <Button
                onClick={() => {
                  const farmUrl = registeredFarmData?.url_friendly_name || urlFriendlyName;
                  navigate(`/${farmUrl}/products`, { replace: true });
                }}
                size="large"
              >
                Go to Products Page
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterProducerScreen;
